import { useEffect, useRef, useState } from 'react';

export default function EvidenceSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);



  return (
    <section id="evidence" ref={ref} className="py-16 md:py-24 bg-[#050608] relative overflow-hidden">
      {/* Blue glow */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[400px] rounded-full bg-[#1E6BFF]/6 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        <div
          className={`mb-10 md:mb-14 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="h-0.5 w-10 bg-[#1E6BFF]" />
            <span className="text-[#5A9AFF] text-xs font-bold tracking-[0.25em] uppercase">Evidence</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#E8EFFF] mb-4 leading-tight">
            感覚ではなく、<br />
            <span className="bg-gradient-to-r from-[#5A9AFF] to-[#1E6BFF] bg-clip-text text-transparent">データで証明</span>する。
          </h2>
          <p className="text-[#C0CBDD] text-base md:text-lg max-w-2xl leading-relaxed">
            フィジオショットの効果は「なんとなく良かった」ではありません。
            研究データと臨床実績に裏付けられた、科学的根拠のある技術です。動物実験ではGAMMAは炎症性マーカー値を軽減するというデータも得られています。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-6">
            <div className="w-full rounded-xl overflow-hidden border border-[#1A2545]">
              <img
                src="https://storage.readdy-site.link/project_files/236a6c9f-17a8-4010-bd30-85bf6da231de/34fce0db-8114-4851-a6e7-c9e469d23f02_-2026-03-27-0.56.15.png?v=90f6b56b68eabf8c5a8e9e91a3105d1f"
                alt="エビデンス資料"
                className="w-full h-auto object-cover object-top"
              />
            </div>
          </div>
        </div>

        <div
          className={`transition-all duration-700 delay-400 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="space-y-4">
            {[
              {
                icon: 'ri-stethoscope-line',
                title: '医師監修のもとで設計',
                desc: 'フィジオショットのプロトコルは、韓国の専門医による監修のもとで設計されています。韓国・中国・ベトナムでは医療機関での使用にも対応。',
              },
              {
                icon: 'ri-shield-check-line',
                title: '韓国特許取得済み技術',
                desc: 'GAMMA技術は韓国特許を取得。独自開発された輻射メカニズムは革新的なアプローチだと考えています。',
              },
              {
                icon: 'ri-global-line',
                title: '2025年5月 日本初上陸',
                desc: '韓国での実績を経て、2025年5月に日本に上陸。すでに全国34施設で導入され、各地で高い評価を受けています。',
              },
            ].map((item) => (
              <div key={item.title} className="flex gap-4 bg-[#0D1020] border border-[#1A2545] hover:border-[#1E6BFF]/40 rounded-xl p-4 transition-all">
                <div className="w-9 h-9 flex items-center justify-center bg-[#0A1535] border border-[#1A2F60] rounded-lg shrink-0">
                  <i className={`${item.icon} text-[#1E6BFF] text-base`} />
                </div>
                <div>
                  <h4 className="text-[#E8EFFF] font-black text-sm mb-1">{item.title}</h4>
                  <p className="text-[#D0DDF0] text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="relative rounded-2xl overflow-hidden w-full h-56 md:h-[340px] border border-[#1A2545] mt-4">
            <img
              src="https://static.readdy.ai/image/1153be8c8a2f65c98102af48ca4fb482/d17dfe80268a7f9657a146d35e476d8c.png"
              alt="医師監修・研究エビデンス"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050608]/50 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#1E6BFF]/10 via-transparent to-transparent" />
          </div>

          {/* 症例動画 */}
          <div className="mt-6 bg-[#0D1020] border border-[#1A2545] rounded-2xl overflow-hidden">
            <div className="px-5 md:px-7 pt-5 md:pt-6 pb-4 flex items-center gap-3 border-b border-[#1A2545]">
              <div className="w-8 h-8 flex items-center justify-center bg-[#0A1535] border border-[#1A2F60] rounded-lg shrink-0">
                <i className="ri-play-circle-line text-[#1E6BFF] text-base" />
              </div>
              <div>
                <p className="text-[#E8EFFF] font-black text-sm md:text-base leading-snug">
                  症例動画 — 1年間痛みに苦しんでいた社会人野球選手
                </p>
                <p className="text-[#D0DDF0] text-xs mt-0.5">フィジオショット施術後の回復プロセスをご覧ください</p>
              </div>
            </div>
            <div className="w-full flex justify-center bg-black">
              <video
                src="https://storage.readdy-site.link/project_files/236a6c9f-17a8-4010-bd30-85bf6da231de/5ee9be98-7b37-47bb-b5d0-b4a89aa38254_-2.mp4?v=53ec7513b1914bec1871b9b889020b2e"
                controls
                autoPlay
                muted
                loop
                playsInline
                className="w-full max-w-[240px] h-auto block"
                aria-label="1年間痛みに苦しんでいた社会人野球選手の症例動画"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
